/**
 * Platform ports — the ONLY seam between the headless chat core and the device.
 *
 * Purity rule (load-bearing, reviewer-checked): everything under `src/chat/core/**`
 * reaches the outside world exclusively through these interfaces. Core imports NOTHING
 * from React, Fluent, Capacitor, or axios. Concrete adapters that bind a port to
 * Capacitor / the browser live OUTSIDE core (`src/chat/platform/`, C0 wiring); tests
 * inject Node fakes. See docs/superpowers/plans/2026-07-06-chat-c1-tasks.md.
 *
 * C1.1 consumes only `SqliteDriverPort` (the SQLite store's SQL seam). The remaining
 * ports are the pinned architectural seam the later C1 tasks build on:
 *   - HttpPort / FilesystemPort / NetworkPort / ClockPort / IdPort  → C1.2 (outbox/drain)
 *   - HttpPort / WebSocketPort / ClockPort                         → C1.4 (transport)
 * They are declared now, with no core consumer yet, because they are required
 * scaffolding (the seam reviewers check purity against), not speculative abstractions.
 */

/** A value bindable to a positional SQL parameter. */
export type SqlValue = string | number | null;

/** Result of a mutating SQL statement. */
export interface SqlRunResult {
    /** Rows inserted/updated/deleted by the statement. */
    changes: number;
}

/**
 * The SQLite driver seam. Async-shaped so a single interface fits both a
 * synchronous test driver (better-sqlite3) and the asynchronous Capacitor plugin
 * bridge (`src/chat/platform/`, device-only). The SQLite adapter's real logic runs
 * against this seam, so it is fully testable off-device.
 *
 * Driver assumption — GUARANTEED by `SqliteStore`, not assumed of engine callers: no
 * concurrent statements run on the same connection while a transaction is open — the
 * atomic BEGIN/COMMIT/ROLLBACK boundary depends on it. The C1 engines legitimately
 * share one store from independent async loops, so `SqliteStore` serializes every
 * operation through an internal FIFO queue (a whole `transaction` bracket is one queue
 * slot); a driver binding may therefore treat each call as exclusive. On an async
 * bridge this is load-bearing: an interleaved statement would silently join an open
 * transaction. Spec: store/__tests__/store-serialization.test.ts.
 */
export interface SqliteDriverPort {
    /** Execute one or more statements with no parameters (DDL / migrations). */
    exec(sql: string): Promise<void>;
    /** Run a mutating statement; returns the affected-row count. */
    run(sql: string, params?: SqlValue[]): Promise<SqlRunResult>;
    /** Fetch the first matching row, or `undefined` when none match. */
    get<T>(sql: string, params?: SqlValue[]): Promise<T | undefined>;
    /** Fetch all matching rows. */
    all<T>(sql: string, params?: SqlValue[]): Promise<T[]>;
    /**
     * Run `work` inside a single atomic transaction: COMMIT on resolve, ROLLBACK on
     * any throw/rejection. Reads and writes issued from `work` (via this same driver)
     * are part of the transaction.
     *
     * The transaction MUST acquire the write lock at start (SQLite: `BEGIN IMMEDIATE`).
     * Store transactions read before their first write; under the two-writer protocol a
     * deferred BEGIN would take a read snapshot the native drainer's commit invalidates,
     * and the write upgrade then fails with SQLITE_BUSY_SNAPSHOT outside busy_timeout.
     * An immediate BEGIN waits under busy_timeout instead — the pinned contention
     * behavior (two-writer-protocol.test.ts).
     */
    transaction<T>(work: () => Promise<T>): Promise<T>;
    /** Close the underlying connection. */
    close(): Promise<void>;
}

/** The header a response carries, set to `1`, when the device served it from its offline copy on disk. */
export const OFFLINE_CACHE_HEADER = 'X-Maper-Offline-Cache';

/** A fetch-shaped HTTP response. */
export interface HttpResponse {
    status: number;
    ok: boolean;
    headers: { get(name: string): string | null };
    json(): Promise<unknown>;
    text(): Promise<string>;
    arrayBuffer(): Promise<ArrayBuffer>;
}

/** A fetch-shaped HTTP request. */
export interface HttpRequest {
    method: string;
    url: string;
    headers?: Record<string, string>;
    body?: string | ArrayBuffer | Blob | FormData;
    /** Cancels the request; the binding then rejects. */
    signal?: AbortSignal;
    /**
     * Overrides the binding's timeout for this request; 0 means no limit. Only the app's axios
     * requests use it, which have no timeout unless they set one. The chat core never does.
     */
    timeoutMs?: number;
    /** Overrides the binding's credentials mode, for axios's `withCredentials: false`. */
    credentials?: RequestCredentials;
}

/**
 * Fetch-shaped HTTP transport. On device this binds to CapacitorHttp (native cookie
 * auth); in tests, a fake. Consumed by C1.2 (send/drain) and C1.4 (typed API layer).
 *
 * Caller obligation for bindings: `fetch` must ALWAYS settle — the platform binding
 * enforces a request timeout (a timeout rejection classifies as a network error in the
 * drain policy). A never-settling fetch wedges the single-flight outbox engine for the
 * process lifetime: its pass awaits the in-flight request and only a restart recovers.
 */
export interface HttpPort {
    fetch(request: HttpRequest): Promise<HttpResponse>;
}

/**
 * Captured-media access in the app container (`@capacitor/filesystem` on device).
 * Capture writes the durable file before composing the outbox row; the media recipe
 * later reads the same path for upload.
 *
 * ALWAYS-SETTLE (same obligation HttpPort documents): `read` runs on the outbox's
 * single-flight drain pass, and `HttpResponse.json()/text()` sit on the same pass —
 * a never-settling call wedges the engine identically to a hung fetch. Bindings must
 * guarantee settlement (a deadline or the platform's own).
 */
export interface FilesystemPort {
    write(path: string, bytes: Uint8Array): Promise<void>;
    read(path: string): Promise<ArrayBuffer>;
    /** A URL the WebView can load the file from directly, where the platform serves files. */
    url?(path: string): Promise<string>;
}

/** A sent medium the outbox no longer needs: its file under Directory.Data, the URL the server gave it, and its MIME type. */
export interface ReleasedMedia {
    path: string;
    url: string | null;
    /** The attachment's MIME type. */
    mime: string;
}

/** Network reachability + online/offline transitions (`@capacitor/network`). C1.2. */
export interface NetworkPort {
    isOnline(): Promise<boolean>;
    /** Subscribe to online/offline transitions; returns an unsubscribe function. */
    onStatusChange(listener: (online: boolean) => void): () => void;
}

/**
 * Injectable clock + timers, so tests can fast-forward backoff/jitter (C1.2) and
 * heartbeat/reconnect schedules (C1.4) without real waiting.
 */
export interface ClockPort {
    /** Epoch milliseconds. */
    now(): number;
    /** Schedule `callback` after `ms`; returns a cancel function. */
    setTimeout(callback: () => void, ms: number): () => void;
}

/** Identifier minting. `uuidv7` is time-ordered — client_uuid is stamped at compose. */
export interface IdPort {
    uuidv7(): string;
}

/**
 * A minimal WebSocket handle — the DOM `WebSocket` API's subset the chat signal channel
 * (transport/socket.ts) actually uses, decoupled from the global so tests drive frames
 * deterministically through a fake. Finalized for C1.4.
 *
 * Handler-assignment shape (`onopen = …`) deliberately mirrors the DOM `WebSocket`, so
 * the platform binding (`src/chat/platform/`, C0) is a thin pass-through to the browser /
 * Capacitor socket with no adapter logic. Frames are strings both ways: the client sends
 * only JSON (`send(JSON.stringify(...))`) — the server closes the socket on non-JSON
 * input (channels' decode contract), so a binding must never forward binary as text.
 *
 * Callback-timing contract (a binding MUST honor it, and fakes MUST mimic it): the DOM
 * `WebSocket` never fires `onopen`/`onclose`/`onmessage` synchronously from within
 * `connect()` — always on a later event-loop turn. `socket.ts` attaches its handlers on
 * the line after `connect()` returns and relies on this: a synchronous open/close would
 * be missed.
 */
export interface WebSocketLike {
    /** Send one text frame (the chat client sends only `JSON.stringify(...)` payloads). */
    send(data: string): void;
    /** Begin closing; the handle later fires `onclose`. Idempotent (a second close is a no-op). */
    close(code?: number, reason?: string): void;
    onopen: (() => void) | null;
    /** Fires once on close, whether client- or server-initiated (a bad ticket → the server denies the handshake pre-accept → the client sees close 1006). */
    onclose: ((event: { code: number; reason: string }) => void) | null;
    onerror: ((error: unknown) => void) | null;
    onmessage: ((event: { data: string }) => void) | null;
}

/**
 * Opens WebSocket connections. On device/web this binds to the platform `WebSocket`
 * (`new WebSocket(url, protocols)` → the handle above); in tests, a scriptable fake.
 * `connect` returns synchronously (like the DOM constructor) — the socket is not yet
 * open. `protocols` is the `Sec-WebSocket-Protocol` offer: the chat socket carries the app
 * subprotocol plus the WS ticket there (transport/socket.ts), never on the URL.
 */
export interface WebSocketPort {
    connect(url: string, protocols?: string[]): WebSocketLike;
}
